import { useCallback, useEffect, useRef } from "react";
import clsx from "clsx";

import { useAppDispatch, useAppSelector } from "../redux";
import { fetchNotifications, setFilters, setPagination } from "../redux/reducers/notificationSlice";
import { View, Text, Icon, Spinner, SettingsList } from "./base";
import { NotificationItem } from "./NotificationItem";

interface NotificationListProps {
  category?: string;
  showFilters?: boolean;
  limit?: number;
}

export const NotificationList = ({ category, showFilters = false, limit = 50 }: NotificationListProps) => {
  const dispatch = useAppDispatch();
  const { notifications, isLoading, isLoadingMore, hasMore, error, filters, pagination, unreadCount } = useAppSelector((state) => state.notifications);

  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // Refetch from page 1 when filters/category change.
  useEffect(() => {
    dispatch(setPagination({ page: 1 }));
    dispatch(fetchNotifications({ category, unread: filters.unread, page: 1, limit }));
  }, [dispatch, category, filters.unread, limit]);

  const loadMore = useCallback(() => {
    if (isLoading || isLoadingMore || !hasMore) return;
    dispatch(fetchNotifications({ category, unread: filters.unread, page: pagination.page + 1, limit }));
  }, [dispatch, category, filters.unread, isLoading, isLoadingMore, hasMore, pagination.page, limit]);

  // IntersectionObserver-driven infinite scroll.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMore();
        }
      },
      { rootMargin: "200px" },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMore]);

  const handleFilterChange = (filterName: "category" | "unread", value: any) => {
    dispatch(setFilters({ [filterName]: value }));
  };

  if (isLoading && notifications.length === 0) {
    return (
      <View className="flex items-center justify-center py-16">
        <Spinner
          size={28}
          variant="brand"
        />
      </View>
    );
  }

  if (error) {
    return (
      <View className="p-6 text-center">
        <Text className="text-sm text-red-500 dark:text-red-400">Error loading notifications: {error}</Text>
      </View>
    );
  }

  return (
    <View className="flex flex-col gap-4 mt-6">
      {showFilters && (
        <View className="grid grid-cols-2 gap-1 p-1 rounded-lg bg-gray-100 dark:bg-gray-800/50 self-start w-44">
          <FilterPill
            label="All"
            active={!filters.unread}
            onClick={() => handleFilterChange("unread", undefined)}
          />
          <FilterPill
            label="Unread"
            count={unreadCount}
            active={filters.unread === true}
            onClick={() => handleFilterChange("unread", true)}
          />
        </View>
      )}

      {notifications.length === 0 ? (
        <View className="p-12 text-center">
          <Icon
            name="notification"
            size={40}
            className="text-gray-300 dark:text-gray-600 mx-auto mb-3"
          />
          <Text className="text-sm font-medium text-gray-900 dark:text-white mb-1 block">No notifications</Text>
          <Text className="text-xs text-gray-500 dark:text-gray-400 block">You're all caught up!</Text>
        </View>
      ) : (
        <SettingsList className="divide-y-0">
          {notifications.map((notification, index) => (
            <View
              key={notification.id}
              className={index === 0 ? "" : "border-t border-gray-100 dark:border-gray-800"}
            >
              <NotificationItem notification={notification} />
            </View>
          ))}
        </SettingsList>
      )}

      {/* Sentinel + loading footer for infinite scroll */}
      {notifications.length > 0 && (
        <View
          ref={sentinelRef}
          className="flex items-center justify-center py-4"
        >
          {isLoadingMore ? (
            <Spinner
              size={20}
              variant="brand"
            />
          ) : !hasMore ? (
            <Text className="text-xs text-gray-400 dark:text-gray-500">No more notifications</Text>
          ) : null}
        </View>
      )}
    </View>
  );
};

interface FilterPillProps {
  label: string;
  count?: number;
  active: boolean;
  onClick: () => void;
}

const FilterPill = ({ label, count, active, onClick }: FilterPillProps) => (
  <button
    type="button"
    onClick={onClick}
    className={clsx(
      "px-3 py-1.5 rounded-md text-xs font-medium transition-colors flex items-center justify-center gap-1.5",
      active
        ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
        : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white",
    )}
  >
    {label}
    {count !== undefined && count > 0 && (
      <Text
        className={clsx(
          "text-[10px] font-semibold px-1.5 py-0.5 rounded-full",
          active ? "bg-brand/10 text-brand dark:bg-cyan-400/20 dark:text-cyan-400" : "bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400",
        )}
      >
        {count}
      </Text>
    )}
  </button>
);
